import React, { FC, useEffect, useState } from "react";
import { Table, Form, Typography } from "antd";
import { Helmet } from "react-helmet";
import { useMercuryContext } from "../../context";

import AddApplicationSetting from "./AddApplicationSetting";
import EditApplicationSetting from "./EditApplicationSetting";
import SettingHistoryDrawer from "./SettingHistoryDrawer";
import { DeleteOutlined, EditOutlined, HistoryOutlined } from "@ant-design/icons";
import { useDrawerScrollMargin } from "common/utils/useScrollPosition";
import PermissionWrapper from "shared/components/PermissionWrapper";
import { checkPermissions } from "common/utils/checkPermissions";
import { failedNotification, successfulNotification } from "common/utils/notifications";
import Breadcrumbs from "stories/bread-crumb/Breadcrumbs";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
interface SettingRow {
  key: string;
  value: any;
  reason: string;
  description: string;
  category: string;
}

const ManageApplicationSettingsPage: FC = () => {
  const { Link } = Typography;
  const context = useMercuryContext();

  const [data, setData] = useState<SettingRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [visibleNew, setVisibleNew] = useState(false);
  const [visibleEdit, setVisibleEdit] = useState(false);
  const [visibleHistory, setVisibleHistory] = useState(false);

  const [editingRow, setEditingRow] = useState<SettingRow | null>(null);
  const [historyKey, setHistoryKey] = useState("");

  const [newForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const { confirm } = Modal;

  const showDeleteConfirm = (key:any) => {
    confirm({
      title: "Delete this setting?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        deleteSetting(key);
      },
    });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await context.coreClient.get(
        "core-v1/application-settings"
      );

      const rows: SettingRow[] = Object.entries(data).map(
        ([key, obj]: [string, any]) => ({
          key,
          value: obj?.value ?? "",
          category: obj?.category,
          reason: obj?.reason,
          description: obj?.description

        })
      );

      setData(rows);
    } catch {
      failedNotification("Failed to load settings");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const upsertSetting = async (values: any) => {
    let parsedValue;
    try {
      parsedValue = JSON.parse(values.value);
    } catch {
      parsedValue = values.value;
    }
    await context.coreClient.post("core-v1/application-settings", {
      key: values.key,
      value: parsedValue,
      reason: values.reason,
      description: values.description,
      category: values.category

    });
    successfulNotification(
      "Setting Saved",
      "Setting has been created successfully."
    );
    newForm.resetFields();
    editForm.resetFields();
    loadData();
  };

  const deleteSetting = async (key: string) => {
    await context.coreClient.delete(
      `core-v1/application-settings/${key}`
    );
    successfulNotification(
      "Setting Deleted",
      "Setting has been deleted successfully."
    );
    loadData();
  };

  const columns = [
    {
      title: "Key",
      dataIndex: "key",
    },
    {
      title: "Value",
      dataIndex: "value",
      render: (v: any) => (
        <Typography.Text code style={{ whiteSpace: "pre-wrap", fontWeight: "bold" }}>
          {v}
        </Typography.Text>

      ),
    },
    {
      title: "Actions",
      render: (_: any, row: SettingRow) => (
        <>
          <PermissionWrapper
            hasPermissions={hasEditAccess}
            children={
              <Link
                onClick={() => {
                  setEditingRow(row);
                  editForm.setFieldsValue({
                    key: row.key,
                    value: row.value,
                  });
                  setVisibleEdit(true);
                }}
                style={{ marginRight: "10px" }}
              >
                <EditOutlined /> Edit
              </Link>
            } />
          <PermissionWrapper
            hasPermissions={hasReadAccess}
            children={
              <Link
                onClick={() => {
                  setHistoryKey(row.key);
                  setVisibleHistory(true);
                }}
                style={{ marginRight: "10px" }}
              >
                <HistoryOutlined /> History
              </Link>
            } />


          <PermissionWrapper hasPermissions={hasDeleteAccess} children={

            <Link style={{ marginRight: "10px" }} onClick={() => showDeleteConfirm(row.key)} > <DeleteOutlined /> Delete </Link>
          } />
        </>
      ),
    },
  ];
  const isVisible = visibleNew || visibleEdit || visibleHistory;
  useDrawerScrollMargin(undefined, isVisible);
  const userPermissions = context.userDetails?.permissions;

  const hasCreateAccess = checkPermissions(userPermissions, [
    {
      customerApplication: "Mercury",
      permissionName: "Settings",
      action: "create",
    },
  ]);

  const hasEditAccess = checkPermissions(userPermissions, [
    {
      customerApplication: "Mercury",
      permissionName: "Settings",
      action: "edit",
    },
  ]);

  const hasDeleteAccess = checkPermissions(userPermissions, [
    {
      customerApplication: "Mercury",
      permissionName: "Settings",
      action: "delete",
    },
  ]);
  const hasReadAccess = checkPermissions(userPermissions, [
    {
      customerApplication: "Mercury",
      permissionName: "Settings",
      action: "read",
    },
  ]);
  return (
    <div className="manage-page manage-app-settings-page" style={{ padding: "0 10px" }}>
      <Helmet>
        <title>Application Settings - Mercury © RWS</title>
      </Helmet>
      <Breadcrumbs
        items={[
          {
            key: "application-settings",
            label: "Application Settings"
          }
        ]}
        />
      
      <PermissionWrapper
        hasPermissions={hasCreateAccess}
        children={
          <AddApplicationSetting
            hasCreateAccess={hasCreateAccess}
            handlers={{
              showDrawer: () => setVisibleNew(true),
              closeDrawer: () => setVisibleNew(false),
              onFinish: upsertSetting,
            }}
            values={{
              visible: visibleNew,
              form: newForm,
            }}
          />
        }
      />

      <Table
        rowKey="key"
        className="manage-page-table"
        pagination={{
          position: ["bottomCenter"],
          defaultPageSize: 20,
        }}
        loading={loading}
        columns={columns}
        dataSource={data}
        style={{ width: "100%" }}
      />

      {editingRow && (
        <EditApplicationSetting
          handlers={{
            closeDrawer: () => setVisibleEdit(false),
            onFinish: upsertSetting,
          }}
          values={{
            visible: visibleEdit,
            form: editForm,
            editingRow,
          }}
        />
      )}

      {visibleHistory && (
        <SettingHistoryDrawer
          hasEditAccess={hasEditAccess}
          settingKey={historyKey}
          visible={visibleHistory}
          onClose={() => setVisibleHistory(false)}
          apiClient={context.coreClient}
          reloadParent={loadData}
        />
      )}
    </div>
  );
};
export default ManageApplicationSettingsPage;
